import {
  Box,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Radio,
  RadioGroup,
  Select,
  TextField,
  Typography,
  Checkbox,
} from '@material-ui/core';
import { KeyboardDateTimePicker, MuiPickersUtilsProvider } from '@material-ui/pickers';
import { Canceler } from 'axios';
import { cloneDeep, remove } from 'lodash';
import moment, { Moment } from 'moment-timezone';
import MomentUtils from '@date-io/moment';
import React, { Fragment, useCallback, useEffect, useRef, useState } from 'react';
import { BlockPicker } from 'react-color';
import { arrayMove } from 'react-sortable-hoc';
import { LinkedSegments } from '../../../components/Curation/Sections';
import { Help } from '../../../components/Help';
import { ImageUpload } from '../../../components/ImageUpload';
import { ScrollableStoryListView } from '../../../components/ScrollableStoryListView';
import { SubmitControl } from '../../../components/SubmitControl';
import useBackdrop from '../../../hooks/useBackdrop';
import api from '../../../services/api/base';
import {
  CollectionType,
  ICollection,
  ICollectionDisplayType,
  IDisplayStyle,
  ISegmentCuration,
  IStory,
  IStoryGenreTextMap,
} from '../../../services/types';
import { getPathnameFromURI, replace } from '../../../services/utils';
import useStyles from './styles';
import { uploadFile } from '../../../services/api/S3';
import { getDisplayStyles } from '../../../services/api/DisplayStyle';
import { useCollection, useCollectionMutation } from '../../../hooks/queries/useCollection';
import { CopyToClipboard } from '../../../components/CopyToClipboard';

interface IProps {
  trackingId?: string;
  onCreated?: (data: ICollection) => void;
  onError?: (error: Error) => void;
}

const CollectionView = (props: IProps): JSX.Element => {
  const DefaultImage = `featured/collection_default_banner.png`;

  const classes = useStyles();
  const backdrop = useBackdrop();

  // states
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [pendingUpload, setPendingUpload] = useState<boolean>();
  const [label, setLabel] = useState<string>('');
  const [title, setTitle] = useState<string>('');
  const [internalTitle, setInternalTitle] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [image, setImage] = useState<string>('');
  const [fileName, setFileName] = useState<string>('');
  const [bgColor, setBgColor] = useState<string>('#ff008a');
  const [subBgColor, setSubBgColor] = useState<string>('#7A3EED');
  const [contentLayout, setContentLayout] = useState<number>(2);
  const [contentType, setContentType] = useState<string>('fictionSerials');
  const [showRanking, setShowRanking] = useState<boolean>(false);
  const [randomOrder, setRandomOrder] = useState<boolean>(false);
  const [isOnboarding, setIsOnboarding] = useState<boolean>(false);
  const [displayType, setDisplayType] = useState<ICollectionDisplayType>(ICollectionDisplayType.General);
  const [discountRate, setDiscountRate] = useState<number | ''>('');
  const [selectedDisplayStyleId, setSelectedDisplayStyleId] = useState<number>();
  const [displayStyles, setDisplayStyles] = useState<IDisplayStyle[]>([]);
  const [selectableDisplayStyles, setSelectableDisplayStyles] = useState<IDisplayStyle[]>([]);
  const [createdAt, setCreatedAt] = useState<string>();
  const [expiredAt, setExpiredAt] = useState<Moment>(moment().add(1, 'hour').startOf('hour'));
  const [stories, setStories] = useState<IStory[]>([]);
  const [currentTrackingId, setCurrentTrackingId] = useState<string>('');
  const [validationMessages, setValidationMessages] = useState<string[]>([]);
  const [linkedCurations, setLinkedCurations] = useState<ISegmentCuration[]>();
  const [isOnboardingEnabled, setIsOnboardingEnabled] = useState<boolean>(false);
  const [isImageHidden, setIsImageHidden] = useState<boolean>(false);

  const { data: collectionFromQuery, isLoading: isCollectionLoading } = useCollection(props.trackingId);
  const { createOrUpdate } = useCollectionMutation();

  // refs
  const originalData = useRef<Partial<ICollection>>({
    label: '',
    title: '',
    internalTitle: '',
    description: '',
    coverUrl: '',
    backgroundColor: '#ff008a',
    layoutColumns: 2,
    type: CollectionType.FictionSerials,
    showRanking: false,
    displayType: ICollectionDisplayType.General,
    stories: [],
    isOnboarding: false,
    isImageHidden: false,
  });
  const displayStylesCanceler = useRef<Canceler>(() => undefined);
  const autoGenerated = useRef<boolean>(false);

  // handlers
  const onFileLoaded = async (file: File, blob: Blob, imageContent: string) => {
    setImage(imageContent);
    setFileName(file.name);

    try {
      setPendingUpload(true);
      const result = await uploadFile('collection_image.jpg', blob);

      if (result?.data && result?.status === 200) {
        setImage(result.data.url);
      }
    } catch (e) {
      console.error(e);
    } finally {
      setPendingUpload(false);
    }
  };

  const onKeyDownImage = (e: React.KeyboardEvent) => {
    e.preventDefault();

    if (e.key === 'Backspace') {
      setFileName('');
      setImage('');
    }
  };

  const handleClickSubmit = async () => {
    try {
      backdrop.show();

      const payload: Partial<ICollection> = stateToPayload();

      payload.storyGenreTextMaps = stories.reduce<IStoryGenreTextMap[]>((acc, cur) => {
        const { id: storyId, storyGenreText } = cur;

        const title = cur.genre?.title || '';

        if (storyGenreText || title) {
          acc.push({
            storyId,
            storyGenreText: storyGenreText || title,
          });
        }

        return acc;
      }, []);

      const result = await createOrUpdate.mutateAsync(payload);

      if (result) {
        if (!currentTrackingId && originalData.current) {
          setStatesFromData(originalData.current);
        }

        if (currentTrackingId) {
          setIsEditing(false);
        }

        if (props.onCreated) {
          props.onCreated(result as ICollection);
        }
      }
    } catch (e) {
      console.error(e);
      if (props.onError) {
        props.onError(e);
      }
    } finally {
      backdrop.hide();
    }
  };

  const handleDisplayTypeChange = (newDisplayType: ICollectionDisplayType) => {
    if (displayType !== ICollectionDisplayType.Sale && newDisplayType === ICollectionDisplayType.Sale) {
      const selectable = filterSelectableDisplayStyles(newDisplayType);
      const selectedItem = selectable.find(item => item.id === selectedDisplayStyleId);
      if (selectedItem === undefined && selectable.length > 0) {
        setSelectedDisplayStyleId(selectable[0].id);
      }
    }
    setDisplayType(newDisplayType);
    if (newDisplayType === ICollectionDisplayType.General) {
      setIsImageHidden(!!originalData.current.isImageHidden);
    } else {
      setIsImageHidden(false);
    }
  };

  const handleDiscountRateChange = (newDiscountRate: number) => {
    setDiscountRate(newDiscountRate);
    switch (newDiscountRate) {
      case 33:
      case 66:
        setTitle(`${newDiscountRate}% Off Today Only`);
        break;
      case 100:
        setTitle(`Today's Free Reads`);
        break;
      default:
        setTitle('');
        break;
    }
  };

  // apis
  const stateToPayload = (): Partial<ICollection> => {
    return {
      trackingId: currentTrackingId,
      displayType,
      label,
      title,
      internalTitle,
      description,
      coverUrl: getPathnameFromURI(image),
      layoutColumns: contentLayout,
      backgroundColor: bgColor,
      type: contentType as CollectionType,
      showRanking,
      randomOrder,
      displayStyleId: selectedDisplayStyleId,
      targetIds: stories.map(s => (s.seasonId ? { storyId: s.id, seasonId: s.seasonId } : { storyId: s.id })),
      expiredAt: displayType === ICollectionDisplayType.Sale ? expiredAt.toDate() : undefined,
      subBackgroundColor: displayType === ICollectionDisplayType.Sale ? subBgColor : undefined,
      discountRate: displayType === ICollectionDisplayType.Sale ? (discountRate as number) : undefined,
      isOnboarding,
      isImageHidden,
    };
  };

  const getRandomThemedStory = (story: IStory) => {
    const { storyGenreText, themes } = story;
    if (!storyGenreText && themes?.detailed?.length) {
      const themesLength = themes?.detailed?.length ?? 0;
      const randomThemeIndex = Math.floor(Math.random() * themesLength);

      return { ...story, storyGenreText: story.themes?.detailed?.[randomThemeIndex]?.title ?? '' };
    }

    return story;
  };

  const setStatesFromData = (data: Partial<ICollection>) => {
    data.label !== undefined && setLabel(data.label);
    data.title !== undefined && setTitle(data.title);
    data.internalTitle !== undefined && setInternalTitle(data.internalTitle);
    data.description !== undefined && setDescription(data.description);
    setFileName(data.coverUrl || '');
    setImage(data.coverUrl || '');
    setBgColor(data.backgroundColor || '#ff008a');
    data.showRanking !== undefined && setShowRanking(data.showRanking);
    data.randomOrder !== undefined && setRandomOrder(data.randomOrder);
    data.layoutColumns !== undefined && setContentLayout(data.layoutColumns);
    data.type !== undefined && setContentType(data.type);
    data.isOnboarding !== undefined && setIsOnboarding(data.isOnboarding);
    data.isImageHidden !== undefined && setIsImageHidden(data.isImageHidden);

    data.stories !== undefined &&
      setStories(
        data.stories.map((story, idx) => {
          if (data.mappers && data.mappers[idx].seasonId) {
            story.seasonId = data.mappers[idx].seasonId;
          }

          return getRandomThemedStory(story);
        }),
      );

    autoGenerated.current = data.autoGenerated || false;

    setSelectedDisplayStyleId(data.displayStyleId);

    data.displayType && setDisplayType(data.displayType);

    if (data.trackingId !== undefined) {
      setCurrentTrackingId(data.trackingId);
    }

    if (data.createdAt !== undefined) {
      setCreatedAt(data.createdAt);
    }

    setLinkedCurations(data.linkedCurations);

    data.expiredAt !== undefined && setExpiredAt(moment(data.expiredAt));
    data.discountRate !== undefined && setDiscountRate(data.discountRate);
    data.subBackgroundColor !== undefined && setSubBgColor(data.subBackgroundColor);
  };

  const fetchDisplayStyles = async () => {
    displayStylesCanceler.current();

    try {
      const [fetchPromise, cancel] = getDisplayStyles();
      displayStylesCanceler.current = cancel;
      const result = await fetchPromise;

      if (result && result.status === 200 && result.data) {
        const { items } = result.data;
        setDisplayStyles(items);
      }
    } catch (e) {
      if (api.isCancel(e)) return;
    }
  };

  const filterSelectableDisplayStyles = useCallback(
    targetDisplayType => {
      if (targetDisplayType === ICollectionDisplayType.Sale) {
        return displayStyles.filter(item => {
          return (
            item.type === 'card' &&
            ((item.shape === 'vertical-rectangle' && item.numberOfDisplay === 2) ||
              (item.shape === 'horizontal-rectangle' && item.numberOfDisplay === 1))
          );
        });
      }
      return displayStyles;
    },
    [displayStyles],
  );

  // watches
  useEffect(() => {
    if (collectionFromQuery) {
      setStatesFromData(collectionFromQuery);
      originalData.current = collectionFromQuery;
    }
  }, [collectionFromQuery]);

  useEffect(() => {
    const messageSet = new Set<string>();

    if (!title) {
      messageSet.add('A title cannot be empty.');
    }

    if (stories && stories.length === 0) {
      messageSet.add('The collection must have at least one story.');
    }

    if (displayType === ICollectionDisplayType.Sale && discountRate === '') {
      messageSet.add('Select a discount rate.');
    }

    const messages = Array.from(messageSet);

    setValidationMessages(messages);
    const isOnboardingEnabled = stories.filter(story => story.seasonId).length === 0;
    setIsOnboardingEnabled(isOnboardingEnabled);
    if (!isOnboardingEnabled && isOnboarding) {
      setIsOnboarding(false);
    }
  }, [title, stories, displayType, discountRate]);

  useEffect(() => {
    setSelectableDisplayStyles(filterSelectableDisplayStyles(displayType));
  }, [displayType, filterSelectableDisplayStyles]);

  // init
  useEffect(() => {
    fetchDisplayStyles();

    return () => displayStylesCanceler.current();
  }, []);

  const isReadOnly = isEditing === false && !!currentTrackingId;

  if (isCollectionLoading) {
    return <div />;
  }

  return (
    <div className={classes.createAutomatedCollectionWrap}>
      <Box
        display="flex"
        flexDirection="row"
        alignItems="center"
        style={{ width: '100%', marginTop: '10px', marginBottom: '16px', paddingLeft: '4px', paddingRight: '22px' }}
      >
        <Typography variant="h6" component="h3" style={{ flex: 1 }}>
          {!currentTrackingId ? (
            'Create Collection'
          ) : (
            <div>
              {originalData.current?.internalTitle && <span className={classes.titleEmphasize}>{`${originalData.current?.internalTitle} : `}</span>}
              {originalData.current?.title}
            </div>
          )}
        </Typography>

        <SubmitControl
          mode={!currentTrackingId ? 'create' : isEditing ? 'edit' : 'view'}
          validationMessages={validationMessages}
          onSubmitClick={() => {
            handleClickSubmit();
          }}
          onEditClick={() => {
            setIsEditing(true);
          }}
          onCancelClick={() => {
            if (originalData.current) {
              setStatesFromData(originalData.current);
            }

            if (isEditing) {
              setIsEditing(false);
            }
          }}
        />
      </Box>

      <Grid container direction="row" justify="flex-start">
        <Grid item xs={6} className={classes.verticalGrid}>
          <Paper className={classes.contentSettingWrap}>
            <Typography variant="h6" component="h3" className={classes.sectionTitle}>
              Content Setting
            </Typography>

            <Grid container direction="column" justify="flex-start" alignItems="stretch">
              <Box paddingY={2} width="100%">
                <Grid item xs={12}>
                  <FormControl>
                    <InputLabel shrink id="display-type-label" className={classes.inputLabel}>
                      Display Type
                    </InputLabel>

                    <Select
                      labelId="display-type-label"
                      value={displayType || ''}
                      onChange={e => handleDisplayTypeChange(e.target.value as ICollectionDisplayType)}
                      disabled={isReadOnly}
                      className={classes.displayTypeForm}
                    >
                      <MenuItem value={ICollectionDisplayType.General}>General</MenuItem>
                      <MenuItem value={ICollectionDisplayType.Event}>Event</MenuItem>
                      <MenuItem value={ICollectionDisplayType.Sale}>Sale</MenuItem>
                    </Select>
                  </FormControl>
                </Grid>
                <Grid item xs={12}>
                  <FormControlLabel
                    className={classes.infoTextField}
                    label="Is Onboarding Collection?"
                    control={
                      <Checkbox
                        color="primary"
                        disabled={isReadOnly || !isOnboardingEnabled}
                        checked={isOnboarding}
                        onChange={e => {
                          setIsOnboarding(e.target.checked);
                        }}
                      />
                    }
                  />
                </Grid>

                {displayType === ICollectionDisplayType.Sale && (
                  <>
                    <Grid item xs={12}>
                      <FormControl>
                        <InputLabel shrink id="discount-rate-label" className={classes.inputLabel}>
                          Discount Rate
                        </InputLabel>

                        <Select
                          labelId="discount-rate-label"
                          value={discountRate || ''}
                          onChange={e => handleDiscountRateChange(Number(e.target.value))}
                          disabled={isReadOnly}
                          className={`${classes.displayTypeForm} double`}
                          displayEmpty
                        >
                          <MenuItem disabled value={''}>
                            Select a Discount Rate
                          </MenuItem>
                          <MenuItem value={33}>33% Off</MenuItem>
                          <MenuItem value={66}>66% Off</MenuItem>
                          <MenuItem value={100}>Free</MenuItem>
                          <MenuItem value={0}>0%</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                      <FormControl>
                        <InputLabel shrink id="expired-at-label" className={classes.inputLabel}>
                          Timer End At
                        </InputLabel>

                        <MuiPickersUtilsProvider utils={MomentUtils}>
                          <KeyboardDateTimePicker
                            format="llll"
                            required
                            margin="normal"
                            id="date-picker-inline"
                            disabled={isReadOnly}
                            value={expiredAt}
                            minDate={moment().toDate()}
                            minDateMessage=""
                            onChange={data => {
                              if (data) setExpiredAt(data);
                            }}
                            className={classes.datePicker}
                          />
                        </MuiPickersUtilsProvider>
                      </FormControl>
                    </Grid>
                  </>
                )}

                {displayType === ICollectionDisplayType.General && (
                  <Grid item xs={12}>
                    <TextField
                      label="Collection Label"
                      className={classes.infoTextField}
                      value={label}
                      onChange={e => {
                        if (isReadOnly) return;

                        setLabel(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                        classes: {
                          root: classes.inputLabel,
                        },
                      }}
                      InputProps={{
                        classes: {
                          input: classes.tfInput,
                        },
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <TextField
                    label={
                      <span>
                        {'Collection Title (Internal)'}
                        <Help text="Internal title is for the convenience of the administrator. It is not exposed in the Radish app." />
                      </span>
                    }
                    className={classes.infoTextField}
                    value={internalTitle || ''}
                    onChange={e => {
                      if (isReadOnly) return;

                      setInternalTitle(e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    label="Collection Title"
                    className={classes.infoTextField}
                    value={title}
                    required
                    onChange={e => {
                      if (isReadOnly) return;

                      setTitle(e.target.value);
                    }}
                    InputLabelProps={{
                      shrink: true,
                      classes: {
                        root: classes.inputLabel,
                      },
                    }}
                    InputProps={{
                      classes: {
                        input: classes.tfInput,
                      },
                    }}
                  />
                </Grid>

                {displayType === ICollectionDisplayType.General && (
                  <Grid item xs={12}>
                    <TextField
                      label="Collection Description"
                      className={classes.infoTextField}
                      value={description}
                      multiline
                      rowsMax={5}
                      required
                      onChange={e => {
                        if (isReadOnly) return;

                        setDescription(e.target.value);
                      }}
                      InputLabelProps={{
                        shrink: true,
                        classes: {
                          root: classes.inputLabel,
                        },
                      }}
                      InputProps={{
                        classes: {
                          input: classes.tfInput,
                        },
                      }}
                    />
                  </Grid>
                )}

                <Grid item xs={12}>
                  <Box display="flex" alignItems="center">
                    <InputLabel className={classes.inputLabel} style={{ marginRight: 18 }}>
                      Collection Image
                    </InputLabel>
                    {displayType === ICollectionDisplayType.General && (
                      <FormControlLabel
                        label="Check to hide"
                        labelPlacement="end"
                        control={
                          <Checkbox
                            color="primary"
                            disabled={isReadOnly}
                            checked={isImageHidden}
                            onChange={e => {
                              setIsImageHidden(e.target.checked);
                            }}
                          />
                        }
                      />
                    )}
                  </Box>
                </Grid>
                {!isImageHidden && (
                  <Grid item xs={12}>
                    <TextField
                      className={classes.infoTextField}
                      value={fileName}
                      onKeyDown={onKeyDownImage}
                      InputLabelProps={{
                        shrink: true,
                        classes: {
                          root: classes.inputLabel,
                        },
                      }}
                      InputProps={{
                        classes: {
                          input: classes.imageFileName,
                        },
                      }}
                    />
                    <ImageUpload onFileLoaded={onFileLoaded} src={image || DefaultImage} readonly={isReadOnly} isPending={pendingUpload} />
                  </Grid>
                )}
              </Box>

              <Box paddingY={2} width="100%">
                <Typography variant="subtitle1" className={classes.sectionTitle}>
                  Background Color
                </Typography>
                <Grid item xs={12}>
                  <Box className={classes.colorPicker}>
                    <BlockPicker
                      triangle={'hide'}
                      width={'100%'}
                      color={bgColor}
                      colors={['#7A3EED', '#1A1AE8', '#2091F9', '#282828', '#8C3631', '#E82EA1', '#0AC48A', '#FF4E00']}
                      onChange={() => {
                        if (isReadOnly) return;
                      }}
                      onChangeComplete={({ hex }) => {
                        if (isReadOnly) return;
                        setBgColor(hex);
                      }}
                    />
                  </Box>
                </Grid>
              </Box>

              <Box paddingY={2} width="100%">
                <Grid container>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Collection Content Layout
                  </Typography>
                </Grid>

                <Grid item xs={12}>
                  <RadioGroup
                    name="columnType"
                    value={contentLayout}
                    onChange={e => {
                      if (isReadOnly) return;

                      setContentLayout(Number(e.target.value));
                    }}
                    className={classes.radioButtonGroups}
                  >
                    <FormControlLabel value={2} control={<Radio color="primary" />} label="Double Column" />
                    <FormControlLabel value={1} control={<Radio color="primary" />} label="Single Column" />
                  </RadioGroup>
                </Grid>

                {currentTrackingId && (
                  <Fragment>
                    <Typography component="h5" variant="subtitle1">
                      Content Type
                    </Typography>
                    <Typography className={classes.viewInfoItem}>{contentType}</Typography>
                    <Typography component="h5" variant="subtitle1">
                      Generate Type
                    </Typography>
                    <Typography className={classes.viewInfoItem}>
                      {autoGenerated.current ? 'Auto-Generated' : 'Manual'}
                      {autoGenerated.current && currentTrackingId && (
                        <a href={`/home/automated-collection/v/${currentTrackingId}`} style={{ marginLeft: 10 }}>
                          SQL
                        </a>
                      )}
                    </Typography>
                    <Typography component="h5" variant="subtitle1">
                      Tracking ID
                    </Typography>
                    <Typography className={classes.viewInfoItem}>
                      <CopyToClipboard content={currentTrackingId} />
                    </Typography>
                    <Typography component="h5" variant="subtitle1">
                      Content Type
                    </Typography>
                    <Typography component="h5" variant="subtitle1">
                      Created At
                    </Typography>
                    <Typography className={classes.viewInfoItem}>{moment(createdAt).format('LLLL')}</Typography>
                  </Fragment>
                )}

                {!currentTrackingId && (
                  <Fragment>
                    <Grid container>
                      <Typography variant="subtitle1" className={classes.sectionTitle}>
                        Content Type
                      </Typography>
                    </Grid>

                    <Grid item xs={12}>
                      <RadioGroup
                        name="contentType"
                        value={contentType}
                        onChange={e => {
                          setContentType(e.target.value);
                        }}
                        className={classes.radioButtonGroups}
                      >
                        <FormControlLabel
                          value="fictionSerials"
                          control={<Radio color="primary" disabled={Boolean(currentTrackingId)} />}
                          label="Stories"
                        />
                        <FormControlLabel
                          value="chatStories"
                          control={<Radio color="primary" disabled={Boolean(currentTrackingId)} />}
                          label="Chat Stories"
                        />
                      </RadioGroup>
                    </Grid>
                  </Fragment>
                )}

                <Grid container>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Collection Display Style
                  </Typography>

                  <Grid item xs={12}>
                    {selectableDisplayStyles.length > 0 && (
                      <Select
                        displayEmpty
                        fullWidth
                        value={selectedDisplayStyleId || ''}
                        disabled={isReadOnly || displayStyles.length === 0}
                        onChange={e => setSelectedDisplayStyleId(e.target.value as number)}
                        className={classes.radioButtonGroups}
                      >
                        {selectableDisplayStyles.map((ds: IDisplayStyle) => (
                          <MenuItem key={ds.id} value={ds.id}>
                            {ds.title}
                          </MenuItem>
                        ))}
                      </Select>
                    )}
                  </Grid>
                </Grid>

                {displayType === ICollectionDisplayType.Sale && (
                  <Box paddingY={2} width="100%">
                    <Typography variant="subtitle1" className={classes.sectionTitle}>
                      Sale Collection Background Color
                    </Typography>
                    <Grid item xs={12}>
                      <Box className={classes.colorPicker}>
                        <BlockPicker
                          triangle={'hide'}
                          width={'100%'}
                          color={subBgColor}
                          colors={['#7A3EED', '#1A1AE8', '#2091F9', '#282828', '#8C3631', '#E82EA1', '#0AC48A', '#FF4E00']}
                          onChange={() => {
                            if (isReadOnly) return;
                          }}
                          onChangeComplete={({ hex }) => {
                            if (isReadOnly) return;
                            setSubBgColor(hex);
                          }}
                        />
                      </Box>
                    </Grid>
                  </Box>
                )}

                <Grid container>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Show Ranking
                  </Typography>
                  <Grid item xs={12}>
                    <RadioGroup
                      name="showRanking"
                      value={showRanking}
                      onChange={e => {
                        if (isReadOnly) return;
                        setShowRanking(e.target.value === 'true');
                      }}
                      className={classes.radioButtonGroups}
                    >
                      <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
                      <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                    </RadioGroup>
                  </Grid>
                </Grid>

                <Grid container>
                  <Typography variant="subtitle1" className={classes.sectionTitle}>
                    Random Order
                  </Typography>
                  <Grid item xs={12}>
                    <RadioGroup
                      name="randomOrder"
                      value={randomOrder}
                      onChange={e => {
                        if (isReadOnly) return;
                        setRandomOrder(e.target.value === 'true');
                      }}
                      className={classes.radioButtonGroups}
                    >
                      <FormControlLabel value={false} control={<Radio color="primary" />} label="No" />
                      <FormControlLabel value={true} control={<Radio color="primary" />} label="Yes" />
                    </RadioGroup>
                  </Grid>
                </Grid>
              </Box>
            </Grid>
          </Paper>
        </Grid>

        <Grid item xs={6} className={classes.verticalGrid}>
          <Grid container direction="column" justify="flex-start" alignItems="stretch">
            {currentTrackingId && (
              <Grid item xs={12}>
                <Paper className={classes.contentSettingWrap}>
                  <Typography component="h5" variant="h6">
                    Linked ({linkedCurations?.length || 0})
                  </Typography>
                  <LinkedSegments linkedCurations={linkedCurations} />
                </Paper>
              </Grid>
            )}
            <Grid item xs={12}>
              <ScrollableStoryListView
                readonly={!!currentTrackingId && !isEditing}
                availableCountryCountText={originalData.current?.availableCountryCountText}
                stories={stories}
                onAddStory={story => {
                  setStories(ps => [getRandomThemedStory(story), ...ps]);
                }}
                onClickRemove={story => {
                  const cloned = [...stories];

                  remove(cloned, s => s.id === story.id && story.seasonId === s.seasonId);
                  setStories(cloned);
                }}
                onChangeStoryGenreText={(story, text) => {
                  const cloned = [...stories];
                  const deepClonedStory = cloneDeep(story);
                  deepClonedStory.storyGenreText = text;

                  const replaced = replace(cloned, s => s.id === story.id && s.seasonId === story.seasonId, deepClonedStory);

                  setStories(replaced);
                }}
                onSortEnd={(newIndex, oldIndex) => {
                  const cloned = arrayMove([...stories], oldIndex, newIndex);
                  setStories(cloned);
                }}
              />
            </Grid>
          </Grid>
        </Grid>
      </Grid>
    </div>
  );
};

export default CollectionView;
